import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgModule, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { A, NINE, SPACE, Z, ZERO } from '@angular/cdk/keycodes';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mat-select-filter',
  template: `
    <form [formGroup]="searchForm" class="mat-filter" [ngStyle]="{'background-color': color || 'white'}">
      <div>
        <input #input class="mat-filter-input" matInput [placeholder]="placeholder || 'Search...'"
          formControlName="value" (keydown)="handleKeydown($event)">
        <mat-spinner *ngIf="localSpinner" class="spinner" diameter="16"></mat-spinner>
      </div>
      <div *ngIf="noResults" class="noResultsMessage">{{ noResultsMessage }}</div>
    </form>
  `,
  styles: [`
    .mat-filter {
      border-bottom: 1px solid grey;
      border-radius: 0;
      box-shadow: none;
      box-sizing: border-box;
      font-size: inherit;
      padding: 16px;
      position: sticky;
      top: 0;
      z-index: 100;
    }
    .mat-filter-input {
      appearance: none;
      background-color: unset;
      border: 0;
      color: grey;
      outline: none;
      width: 100%;
    }
    .spinner {
      position: absolute;
      right: 16px;
      top: calc(50% - 8px);
    }
    .noResultsMessage {
      font-family: Roboto, Helvetica Neue, sans-serif;
      font-size: 16px;
      margin-top: 10px;
    }
  `],
  standalone: false
})
export class MatSelectFilterComponent implements OnInit, OnDestroy {
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input() array: any[] = [];
  @Input() placeholder: string;
  @Input() color: string;
  @Input() displayMember: string;
  @Input() showSpinner = true;
  @Input() noResultsMessage = 'No results';
  @Input() hasGroup: boolean;
  @Input() groupArrayName: string;

  @Output() filteredReturn = new EventEmitter<any[]>();

  noResults = false;
  localSpinner = false;
  searchForm = this.fb.group({ value: '' });

  private valueChangesSubscription: Subscription;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.valueChangesSubscription = this.searchForm.valueChanges.subscribe(({ value }) => {
      this.filterItems(value || '');
    });

    setTimeout(() => this.input?.nativeElement.focus(), 500);
  }

  handleKeydown(event: KeyboardEvent) {
    if ((event.key && event.key.length === 1) ||
      (event.keyCode >= A && event.keyCode <= Z) ||
      (event.keyCode >= ZERO && event.keyCode <= NINE) ||
      event.keyCode === SPACE) {
      event.stopPropagation();
    }
  }

  ngOnDestroy() {
    this.filteredReturn.emit(this.array || []);
    this.valueChangesSubscription?.unsubscribe();
  }

  private filterItems(searchText: string) {
    const source = this.array || [];
    const normalizedSearch = searchText.toLowerCase();

    if (this.showSpinner) {
      this.localSpinner = true;
    }

    if (!normalizedSearch) {
      this.noResults = false;
      this.filteredReturn.emit(source.slice());
      this.hideSpinner();
      return;
    }

    const filteredItems = this.hasGroup && this.groupArrayName && this.displayMember
      ? this.filterGroupedItems(source, normalizedSearch)
      : source.filter(item => this.itemMatches(item, normalizedSearch));

    this.noResults = filteredItems.length === 0;
    this.filteredReturn.emit(filteredItems);
    this.hideSpinner();
  }

  private filterGroupedItems(source: any[], searchText: string) {
    return source
      .map(item => {
        const itemCopy = { ...item };
        const groupItems = Array.isArray(itemCopy[this.groupArrayName]) ? itemCopy[this.groupArrayName] : [];
        itemCopy[this.groupArrayName] = groupItems.filter(groupItem => this.itemMatches(groupItem, searchText));
        return itemCopy;
      })
      .filter(item => item[this.groupArrayName].length > 0);
  }

  private itemMatches(item: any, searchText: string) {
    const value = this.displayMember ? item?.[this.displayMember] : item;
    return String(value ?? '').toLowerCase().includes(searchText);
  }

  private hideSpinner() {
    setTimeout(() => {
      if (this.showSpinner) {
        this.localSpinner = false;
      }
    }, 200);
  }
}

@NgModule({
  declarations: [MatSelectFilterComponent],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  exports: [MatSelectFilterComponent]
})
export class MatSelectFilterModule {}
